import { useTokens } from '../../tokens';
import { PSection, usePresetBuilder } from '../context.jsx';

const IDENTIFIER_OPTS = [
    { id: 'sku', label: 'SKU (catálogo + vendas)' },
    { id: 'mlb', label: 'MLB (anúncios vendidos)' },
    // Envio ao Full é por inventory: anúncios que compartilham o estoque viram
    // 1 linha, com vendas somadas e o saldo Full DESSE inventory.
    { id: 'inventory', label: 'Inventory ID (Full — vendas somadas)' },
];

/**
 * Toggle de visualização por coluna — sempre tem text/bar; line (sparkline) só
 * pra colunas agregadas com daily slots disponíveis (`CHARTABLE_COLS`). Custom
 * cols com format numérico ganham text/bar mas não line (sem série temporal).
 *
 * Vive em ChartSettings (e não em ColumnSelector) porque é puramente
 * apresentação/chart — usado dentro do ColumnSelector via import explícito,
 * preservando coesão de "tudo chart-related em um lugar".
 *
 * Convenções:
 *   - 'text' (default, ausente do map): mostra só o número agregado.
 *   - 'line': sparkline + número à direita. Disponível apenas se showLine.
 *   - 'bar':  barra horizontal proporcional ao máximo da página.
 */
export function ViewModeToggle({ currentView, onChange, showLine = false }) {
    const T = useTokens();
    const modes = [
        { id: 'text', label: '123', title: 'Texto (número)' },
        ...(showLine ? [{ id: 'line', label: '〰', title: 'Gráfico de linha (sparkline)' }] : []),
        { id: 'bar', label: '▮▮', title: 'Barra horizontal' },
    ];
    return (
        <span style={{ display: 'inline-flex', border: `1px solid ${T.line}`, borderRadius: 3, overflow: 'hidden' }}>
            {modes.map((m, i) => {
                const active = currentView === m.id;
                return (
                    <button
                        key={m.id}
                        type="button"
                        onClick={() => onChange(m.id)}
                        title={m.title}
                        style={{
                            background: active ? T.accent : 'transparent',
                            color: active ? '#fff' : T.muted,
                            border: 'none',
                            borderLeft: i > 0 ? `1px solid ${T.line}` : 'none',
                            padding: '2px 6px',
                            fontSize: 10,
                            fontFamily: T.mono,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            outline: 'none',
                            lineHeight: 1,
                        }}
                    >
                        {m.label}
                    </button>
                );
            })}
        </span>
    );
}

/**
 * ChartSettings section — comprometimento pragmático: a section "ChartSettings"
 * hospeda o seletor de Identificador (chave de agregação das linhas/charts) por
 * dois motivos:
 *
 *   1. Identificador define O QUE cada linha representa, o que muda o
 *      significado de qualquer chart/sparkline na tabela.
 *   2. ViewModeToggle (chart per-column) já vive aqui — manter a section
 *      "chart settings" como o lugar único de configurações de display
 *      facilita encontrar (chart toggles globais futuros caberiam aqui).
 *
 * O hint da section explica isso ao usuário.
 */
export function ChartSettings() {
    const T = useTokens();
    const { draft, patch } = usePresetBuilder();

    return (
        <PSection title="Identificador" hint="Define a chave de agregação das linhas. Colunas são as mesmas nos três modos.">
            <select
                value={draft?.identifier || 'sku'}
                // Colunas são universais — trocar identifier NÃO poda mais
                // a lista. Em modo MLB, o pipeline preenche brand/bling/etc
                // via lookup do SKU do anúncio. Em modo SKU, vendas já são
                // agregadas de todos os MLBs do SKU.
                onChange={(e) => patch({ identifier: e.target.value })}
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: 4,
                    border: `1px solid ${T.line}`,
                    background: T.bg,
                    color: T.ink,
                    fontSize: 13,
                    outline: 'none',
                }}
            >
                {IDENTIFIER_OPTS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </PSection>
    );
}
